import React from 'react';
import { prerenderState } from '../seo/prerenderState';

/**
 * React.lazy that survives a deploy under an open tab.
 *
 * The failure (PostHog, 2026-07-26): "Failed to fetch dynamically imported
 * module: .../assets/MajorMap-<hash>.js". A visitor loads the SPA, we deploy,
 * Vite emits NEW content-hashed chunks, and Firebase Hosting serves only the
 * current release. The already-loaded page still holds the OLD module graph in
 * memory, so navigating to a lazy route requests a chunk that no longer exists.
 *
 * It isn't even a clean 404: the hosting rewrite (`** -> /index.html`) answers
 * the missing chunk with `200 text/html`, so the browser gets HTML where it
 * expected a module and refuses to parse it. Worse, that HTML lands in the
 * HTTP cache under the /assets/** immutable header — sw.js repairs that; this
 * module only handles the stale-shell case, where ONE reload genuinely fixes
 * it because index.html is served no-cache.
 *
 * The retry budget is deliberately simple and MONOTONIC: one automatic reload
 * per browser session, spent forever once used, restored ONLY by the error
 * card's Reload button. Two earlier designs let something else restore it and
 * both failed:
 *   1. A 30-second time window — a second deploy minutes later read as "the
 *      retry already failed" and bounced the user on the error card.
 *   2. Clearing on App mount — App lives in the ENTRY chunk and always mounts,
 *      so the budget came back before the lazy route resolved: fail -> reload
 *      -> mount -> clear -> fail, forever, on a blank page.
 * If you are tempted to make this cleverer, start by explaining why the loop
 * cannot happen. Here it cannot: the flag only ever goes one way.
 */

/** A missing/HTML-instead-of-JS chunk, not an error thrown by the module itself. */
export function isChunkLoadError(error) {
    const message = String(error?.message || error || '');
    return (
        /Failed to fetch dynamically imported module/i.test(message) ||
        /error loading dynamically imported module/i.test(message) ||
        /Importing a module script failed/i.test(message) ||   // Safari
        /Unable to preload CSS/i.test(message) ||
        /'text\/html' is not a valid JavaScript MIME type/i.test(message)
    );
}

// "The session's one automatic reload has been spent." Never expires on its
// own; only the error card's Reload button clears it.
const SPENT_KEY = 'stratia_auto_reload_spent';

// The breadcrumb for a reload has to outlive the reload itself. It cannot be
// sent at reload time: window.location.reload() runs synchronously right
// after, so a dynamic import of the analytics module never gets to resolve —
// the original code did exactly that, and the event never once fired.
const PENDING_EVENT_KEY = 'stratia_stale_reload_event';

/**
 * Spend the session's single auto-reload. False when it is already spent, or
 * when sessionStorage cannot RECORD the spend — an unrecorded reload is
 * exactly how the infinite loop happened, so "can't remember trying" must
 * mean "don't try". The error card is the safe failure; its Reload button
 * still works there.
 */
function claimRetry() {
    try {
        if (sessionStorage.getItem(SPENT_KEY)) return false;
        sessionStorage.setItem(SPENT_KEY, '1');
        return true;
    } catch {
        return false;
    }
}

/**
 * Restore the retry budget. ONLY ever called from an explicit user action
 * (the error card's Reload button). Never on mount, never on a timer —
 * automatic clearing is what turned a bounded retry into an infinite loop.
 */
export function clearReloadMarker() {
    try {
        sessionStorage.removeItem(SPENT_KEY);
    } catch { /* ignore */ }
}

/**
 * Emit the breadcrumb for a reload that already happened, then forget it.
 * Called once per boot from AnalyticsListener.
 *
 * TELEMETRY ONLY. This must never touch SPENT_KEY: restoring the retry budget
 * from a boot-time hook is precisely the shape of the infinite-loop bug, and
 * a test pins that this function does not do it.
 *
 * @param {(chunk: string) => void} emit
 * @returns {string|null} the chunk name that was reported, if any
 */
export function flushStaleBuildTelemetry(emit) {
    let name = null;
    try {
        name = sessionStorage.getItem(PENDING_EVENT_KEY);
        if (!name) return null;
        sessionStorage.removeItem(PENDING_EVENT_KEY);
    } catch {
        return null;
    }
    try { emit(name); } catch { /* never let telemetry break boot */ }
    return name;
}

// Every lazy component by its chunk name, so the browser can load a
// prerendered page's chunks BEFORE it hydrates (preloadRouteChunks, #4474).
const REGISTRY = new Map();

/**
 * Load the named lazy components and wait for them. A prerendered page lists
 * the chunks its markup was built from (#root data-route-chunks); main.jsx
 * waits on this before hydrating, so hydration never meets an unloaded chunk.
 *
 * Why it matters (#4474, measured live 2026-10-16 on slow 4G + 4x CPU): the
 * college page's chunk was requested only once the app bundle ran (~3.8 s),
 * and an update reaching the still-dehydrated Suspense boundary before it
 * arrived made React client-render that boundary, which shows its fallback:
 * the painted page became a spinner from ~4.0 s to ~6.8 s. A name loaded here
 * renders synchronously, so there is nothing for the fallback to replace.
 *
 * A name not registered yet (a lazy component inside another lazy chunk) is
 * looked up again once the chunks loaded so far have run; one that never
 * registers is skipped, never waited on. Never rejects: a chunk that fails is
 * left to lazyWithRetry's own reload and the error boundary at render.
 *
 * @param {string[]} names
 * @returns {Promise<void>}
 */
export async function preloadRouteChunks(names) {
    let todo = [...new Set(names || [])];
    for (let round = 0; round < 4 && todo.length; round += 1) {
        const ready = todo.filter((n) => REGISTRY.has(n));
        if (!ready.length) return;
        await Promise.all(ready.map((n) => REGISTRY.get(n).preload().catch(() => {})));
        todo = todo.filter((n) => !ready.includes(n));
    }
}

/**
 * React.lazy plus three things: one reload for a stale build's missing chunk,
 * `preload()` (the same load React will use, started early), and, at build
 * time, a record of every lazy component a prerendered page rendered, so its
 * HTML can name and preload those chunks (prerenderEntry, #4474).
 *
 * @param {() => Promise<any>} factory  the dynamic import
 * @param {string} name                 chunk name: unique, and the file's name
 *                                      (the build finds the chunk by it)
 */
export default function lazyWithRetry(factory, name = 'unknown') {
    let loaded = null;
    let pending = null;
    const load = () => {
        if (!pending) {
            pending = factory().then((mod) => { loaded = mod; return mod; }, (error) => {
                if (!isChunkLoadError(error)) throw error;   // real module error — surface it

                // Spend the session's one auto-reload; if it is already spent the
                // boundary explains it — honest and, critically, terminal.
                if (!claimRetry()) throw error;
                try {
                    sessionStorage.setItem(PENDING_EVENT_KEY, name);
                } catch { /* telemetry is never worth failing a reload over */ }
                window.location.reload();
                // Keep the promise pending so React shows the Suspense fallback
                // during the reload instead of flashing the error boundary.
                return new Promise(() => {});
            });
        }
        return pending;
    };
    // Already loaded (preloaded before hydration): hand React a thenable that
    // resolves synchronously, so the first render does not suspend at all.
    const Lazy = React.lazy(() => (loaded ? { then: (resolve) => resolve(loaded) } : load()));
    function LazyRoute(props) {
        const state = prerenderState();
        if (state) (state.chunks || (state.chunks = [])).push(name);
        return React.createElement(Lazy, props);
    }
    LazyRoute.displayName = `Lazy(${name})`;
    LazyRoute.preload = load;
    LazyRoute.chunkName = name;
    REGISTRY.set(name, LazyRoute);
    return LazyRoute;
}
